import { apiClient } from './client';

/**
 * Client for `/api/overtime/*` (Phase 14, completed post-audit Tier 2).
 * Stays in lockstep with `server/src/routes/overtime/*.routes.ts`.
 */

// ── Ledger ──────────────────────────────────────────────────────────────

export interface LedgerWeekRow {
  employeeMembershipId: string;
  employeeName: string;
  isActive: boolean;
  managerMembershipId: string | null;
  /** Mon..Sun clocked hours. */
  hoursByDay: number[];
  weekTotal: number;
  updatedAt: string | null;
}

export interface LedgerWeek {
  /** Monday of the week, YYYY-MM-DD. */
  weekOf: string;
  rows: LedgerWeekRow[];
}

export async function getLedgerWeek(weekOf?: string): Promise<LedgerWeek> {
  const qs = weekOf ? `?weekOf=${encodeURIComponent(weekOf)}` : '';
  const res = await apiClient.get<{ data: LedgerWeek }>(`/api/overtime/ledger${qs}`);
  return res.data.data;
}

export interface UploadLedgerEntry {
  employeeMembershipId: string;
  /** YYYY-MM-DD — any date inside the target week (anchored to Monday). */
  workweekStartDate: string;
  /** 7 values, Mon..Sun, each 0..24. */
  hoursByDay: number[];
}

export async function uploadLedger(entries: UploadLedgerEntry[]): Promise<{ upserted: number }> {
  const res = await apiClient.post<{ data: { upserted: number } }>('/api/overtime/ledger/upload', {
    entries,
  });
  return res.data.data;
}

// ── Roster ──────────────────────────────────────────────────────────────

export interface RosterRow {
  id: string;
  employeeMembershipId: string;
  employeeName: string;
  employeeEmail: string;
  managerMembershipId: string | null;
  managerName: string | null;
  state: string | null;
  isHourly: boolean;
  isActive: boolean;
  createdAt: string;
}

export async function listRoster(): Promise<RosterRow[]> {
  const res = await apiClient.get<{ data: RosterRow[] }>('/api/overtime/employees');
  return res.data.data;
}

export interface AddRosterEntryRequest {
  employeeMembershipId: string;
  managerMembershipId?: string | null;
  state?: string | null;
}

export async function addRosterEntry(body: AddRosterEntryRequest): Promise<RosterRow> {
  const res = await apiClient.post<{ data: RosterRow }>('/api/overtime/employees', body);
  return res.data.data;
}

export interface UpdateRosterEntryRequest {
  managerMembershipId?: string | null;
  state?: string | null;
  isActive?: boolean;
}

export async function updateRosterEntry(id: string, body: UpdateRosterEntryRequest): Promise<RosterRow> {
  const res = await apiClient.patch<{ data: RosterRow }>(
    `/api/overtime/employees/${encodeURIComponent(id)}`,
    body,
  );
  return res.data.data;
}

// ── Evaluation ──────────────────────────────────────────────────────────

export interface EvaluationSummary {
  organizationId: string;
  evaluatedFor: string;
  employeesEvaluated: number;
  eventsCreated: number;
  dispatchesQueued: number;
  mailsSent: number;
  mailsFailed: number;
  errors: { employeeMembershipId: string; message: string }[];
}

export async function runEvaluationNow(): Promise<EvaluationSummary> {
  const res = await apiClient.post<{ data: EvaluationSummary }>('/api/overtime/evaluate-now');
  return res.data.data;
}
